import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { priceForecastService } from "@/services/priceForecastService";
import { Loader2, Target, TrendingDown, Calendar, MapPin } from "lucide-react";
import { format } from "date-fns";

export default function BuySignals() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [signals, setSignals] = useState<any[]>([]);
  const { toast } = useToast();

  const loadSignals = async () => {
    setIsLoading(true);
    try {
      const data = await priceForecastService.getBuySignals({
        status: 'active',
      });

      setSignals(data);
    } catch (error) {
      console.error('Error loading buy signals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateAll = async () => {
    setIsGenerating(true);

    try {
      const result = await priceForecastService.generateBuySignals(undefined, {
        forecastHorizon: 7,
        minPriceAdvantage: 5,
        minConfidence: 70,
      });

      toast({
        title: "Signals Generated",
        description: `Found ${result.summary.total_signals} buying opportunities`,
      });

      loadSignals();
    } catch (error: any) {
      console.error('Generation error:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate buy signals",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExecute = async (signalId: string) => {
    try {
      await priceForecastService.updateBuySignalStatus(signalId, 'executed');
      toast({
        title: "Signal Executed",
        description: "Buy signal marked as executed",
      });
      loadSignals();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    loadSignals();
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Buy Signals
              </CardTitle>
              <CardDescription>
                AI-powered buying recommendations based on price forecasts
              </CardDescription>
            </div>
            <Button onClick={handleGenerateAll} disabled={isGenerating}>
              {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Generate Signals
            </Button>
          </div>
        </CardHeader>
      </Card>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : signals.length > 0 ? (
        <div className="grid gap-4">
          {signals.map((signal) => (
            <Card key={signal.id} className="border-l-4 border-l-primary">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{signal.product_name}</CardTitle>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(signal.signal_date), 'MMM d, yyyy')}
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {signal.recommended_location}
                      </div>
                    </div>
                  </div>
                  <Badge variant="default" className="flex items-center gap-1">
                    <TrendingDown className="h-3 w-3" />
                    {signal.price_advantage_percent.toFixed(1)}% savings
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Predicted Price</div>
                      <div className="text-2xl font-bold">
                        {signal.predicted_price.toFixed(2)} ETB
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Confidence</div>
                      <div className="text-2xl font-bold">
                        {signal.confidence_score.toFixed(0)}%
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium mb-2">Alternative Locations</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(signal.alternative_prices).map(([location, price]) => (
                        <div key={location} className="flex justify-between p-2 bg-muted rounded">
                          <span>{location}</span>
                          <span className="font-medium">{(price as number).toFixed(2)} ETB</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-2">
                    <div className="text-sm text-muted-foreground">
                      Expires: {format(new Date(signal.expires_at), 'MMM d, yyyy')}
                    </div>
                    <Button onClick={() => handleExecute(signal.id)}>
                      Mark as Executed
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-center">
            <Target className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No active buy signals</p>
            <p className="text-sm text-muted-foreground mt-2">
              Generate signals to find buying opportunities
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
